import { useState, useEffect } from 'react'
import MainNavMenu from '../../menus/MainNavMenu/MainNavMenu'
import { createUserThreshold, deleteUserThreshold, fetchUserThresholdsV2, updateUserThreshold } from './thresholdsApi'
import { ThresholdForm } from './ThresholdForm'
import { UnitsForm } from './UnitsForm'
import messagealert from '../../assets/message-alert-square.svg'
import { thresholdCategories } from './constants'
import { DropDownWithLabel, Input, Spinner } from '../../utils/UtilComponents'
import { ThresholdCategoryData, ThresholdEntry, ThresholdProfiles, Thresholds, Units } from './types'
import clsx from 'clsx'
import useErrorStatus from '../../hooks/UseErrorStatus'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { setCutomThresholdProfile, updateCTU } from '../Users/UserSlice'
import { updateThresholdProfile } from '../../services/UserApiService'
import { ErrorTimeouts } from '../../constants/ErrorTimeouts'

export const CustomThresholds = () => {
  const banner = useErrorStatus()
  const dispatch = useDispatch()

  const [userThresholds, setUserThresholds] = useState<ThresholdCategoryData[]>([])
  const [selection, setSelection] = useState(0)
  const [drafts, setDrafts] = useState<Record<string, { unit: string; params: Record<string, ThresholdEntry> }>>({});
  const [loadingScreen, setLoadingScreen] = useState(true)
  const [editProfileDopDown, setProfileEditDropDown] = useState(true)
  
  //Implementation of profiling logic
  const currProfile = useSelector((state: RootState) => state.user.thresholdProfile)  
  
  const [profileList, setProfileList] = useState<Record<string, string>>({});
  const [modalOpen, setModalState] = useState(false)
  const [profileFormInput, setProfileFormInput] = useState("")
  const [formLoad, setFormLoad] = useState(false)
  const [thresholdProfiles, setThresholdProfiles] = useState<ThresholdProfiles>([])
  const [removeProfileLoad, setRemoveProfileLoad] = useState(false)

  //Implementation of update profile Logic
  const [saveProfileLoad, setSaveProfileLoad] = useState(false)
  const [saveUnitsLoad, setSaveUnitLoad] = useState(false)
  const [updatedUnitsState, setUpdatedUnitsState] = useState(false)
  const [updatedThresholdsState, setUpdatedThresholdsState] = useState(false)


  const processThresholds = (profiles: ThresholdProfiles) => {
    const profileObjectList: Record<string, string> = {};
    const profileBasedThreshold: Record<string, { thresholds: Thresholds; units: Units }> = {};

    for (const threshold of profiles) {      
      profileObjectList[threshold.profile.toUpperCase()] = threshold.profile;
      profileBasedThreshold[threshold.profile] = {
        thresholds: threshold.thresholds,
        units: threshold.units,
      };
    }

    setProfileList(profileObjectList);

    const selectedProfile =
      profileBasedThreshold[currProfile] ?? profileBasedThreshold["default"];

    const initialDrafts: Record<string, { unit: string; params: Record<string, ThresholdEntry> }> = {};

    const processedResponse: ThresholdCategoryData[] = Object.entries(thresholdCategories).map(
      ([key, catData]) => {        
        const currUnit = selectedProfile?.units[key as keyof Units];

        const parameters = Object.entries(catData.keyLabelMap).map(([paramKey, paramLabel]) => {
          const paramData = selectedProfile.thresholds[paramKey as keyof Thresholds];
          return {
            key: paramKey,
            label: paramLabel,
            comparator: paramData?.comparator,
            currentHighRisk: paramData?.currentHighRisk,
            currentMarginalRisk: paramData?.currentMarginalRisk,
          };
        });

        initialDrafts[key] = {
          unit: currUnit,
          params: Object.fromEntries(
            parameters.map((p) => [
              p.key,
              {
                comparator: p.comparator,
                currentHighRisk: p.currentHighRisk,
                currentMarginalRisk: p.currentMarginalRisk,
              },
            ])
          ),
        };

        return {
          key,
          label: catData.label,
          unitOptions: catData.unitOptions,
          unit: currUnit,
          parameters,
        };
      }
    );
    setUserThresholds(processedResponse);
    setDrafts(initialDrafts);
  };
  const updateParamValue = (catKey: string | number, paramKey: string | number, field: any, value: string) => {    
    setUpdatedThresholdsState(true)
    setDrafts((prev) => ({
      ...prev,
      [catKey]: {
        ...prev[catKey],
        params: {
          ...prev[catKey].params,
          [paramKey]: { ...prev[catKey].params[paramKey], [field]: value === "" ? "" : Number(value) },
        },
      },
    }));
  };
  const updateUnit = (catKey: string | number, newUnit: any) => {
    setUpdatedUnitsState(true)
    setDrafts((prev) => ({ ...prev, [catKey]: { ...prev[catKey], unit: newUnit } }));
  };
  const handleThresholdSave = async () => {
    setSaveProfileLoad(true)
    try{
      let updatedBody: Record<string, any> = {}
      updatedBody["thresholds"] = {}
      updatedBody["profile"] = currProfile
      Object.keys(drafts).forEach((category) => {
        Object.entries(drafts[category]["params"]).forEach(([key, val]) => {
          updatedBody["thresholds"][key] = {
            "highRisk" : val["currentHighRisk"],
            "marginalRisk" : val["currentMarginalRisk"]
          }
        })
      })
      //console.log("Saving payload:", updatedBody);
      let updatedThreshold = await updateUserThreshold(updatedBody);
      setThresholdProfiles((prev) => [
        updatedThreshold,
        ...prev.filter((p) => p.profile !== updatedThreshold.profile)
      ]);
      dispatch(updateCTU(updatedThreshold))
      setUpdatedThresholdsState(false)
      banner.addMessage('Thresholds updated', 200, 'success', false, ErrorTimeouts.GENERAL);
    }
    catch(error){
      banner.addMessage(error?.message || 'Error while updating thresholds', 400, 'error', false, ErrorTimeouts.GENERAL);    
    }
    setSaveProfileLoad(false)
  };
  const handleUnitSave = async () => {
    setSaveUnitLoad(true)    
    try{
      let updatedBody: Record<string, any> = {}
      updatedBody["units"] = {}
      updatedBody["profile"] = currProfile
      Object.entries(drafts).forEach(([category, values]) => {
        updatedBody["units"][category] = values['unit']
      })
      console.log("Saving payload:", updatedBody);
      let updatedThreshold = await updateUserThreshold(updatedBody);
      setThresholdProfiles((prev) => [
        updatedThreshold,
        ...prev.filter((p) => p.profile !== updatedThreshold.profile)
      ]);
      dispatch(updateCTU(updatedThreshold))
      banner.addMessage('Units updated', 400, 'success', false, ErrorTimeouts.GENERAL);
      setUpdatedUnitsState(false)
    }
    catch(error){
      banner.addMessage(error?.message || 'Error while updating units', 400, 'error', false, ErrorTimeouts.GENERAL);    
    }
    setSaveUnitLoad(false)
  };
  const handleNewProfile = async () => {
    setFormLoad(true)
    try {
      let newThreshold = await createUserThreshold(profileFormInput)
      await updateProfile(profileFormInput)
      setThresholdProfiles((prev) => [newThreshold, ...prev]);
      dispatch(updateCTU(newThreshold))
      setModalState(false)
      banner.addMessage('New threshold created', 400, 'success', false, ErrorTimeouts.GENERAL);    

    } catch (error) {      
      banner.addMessage(
        error?.message || 'Error while creating new threshold',
        400,
        'error',
        false,
        ErrorTimeouts.GENERAL
      )
    }
    setFormLoad(false)
    setProfileFormInput("")
  };
  const removeProfile = async () => {
    if (confirm('Delete this threshold profile?')) {
      setRemoveProfileLoad(true)
      try {
        await deleteUserThreshold(currProfile)
        let deletedProfile = currProfile
        await updateProfile("default")
        setThresholdProfiles((prev) => [
          ...prev.filter((p) => p.profile !== deletedProfile)
        ]);
        banner.addMessage('Profile removed', 200, 'success', false, ErrorTimeouts.GENERAL)
      } catch (error) {      
        banner.addMessage(
          error?.message || 'Error while creating new threshold',
          400,
          'error',
          false,
          ErrorTimeouts.GENERAL
        )
      }
      setRemoveProfileLoad(false)
    }
  } 
  const fetchCustomThresholds = async () => {
    try {
      const responseThresholds = await fetchUserThresholdsV2();      
      if(!Array.isArray(responseThresholds) || responseThresholds.length == 0){
        let newThreshold = await createUserThreshold("default")
        setThresholdProfiles((prev) => [newThreshold, ...prev]);
      }
      else
        setThresholdProfiles(responseThresholds);
      setLoadingScreen(false)
    } catch (error) {
      banner.addMessage(error?.message || 'Error while fetching thresholds', 400, 'error', false, ErrorTimeouts.GENERAL);
    }
  };
  const updateProfile = async (label: any) => {
    setProfileEditDropDown(false)
    try {
      await updateThresholdProfile(label)
      dispatch(setCutomThresholdProfile(label))
      banner.addMessage('Profile updated successfully', 400, 'success', false, ErrorTimeouts.GENERAL);    
    } catch (error) {
      banner.addMessage(error?.message || 'Error while updating profile', 400, 'error', false, ErrorTimeouts.GENERAL);    
    }
    setProfileEditDropDown(true)
  }

  useEffect(() => { fetchCustomThresholds() }, [])
  useEffect(() => {
    if (thresholdProfiles.length > 0) {
      processThresholds(thresholdProfiles);
    }
  }, [currProfile, thresholdProfiles]);

  return (
    <div className="flex items-center p-4 w-full bg-[#F1F5F9] overflow-y-clip">
      <div className="flex flex-col m-4 w-full rounded-lg bg-[#F8F8F8] h-full border border-[#94a3b8] gap-2 p-4">
        {/* Title div and back button */}
        <div className="flex flex-row items-center">
          <MainNavMenu />
          <div className="text-xl text-[#1e293b] uppercase absolute left-1/2 transform -translate-x-1/2 font-semibold">
            Units & Thresholds
          </div>
        </div>
        {
          loadingScreen ? 
          <div className='flex items-center justify-center h-full w-full bg-[#cbd5e1] rounded-lg animate-bg-pulse'/>
          : 
          <>
            <div className='flex flex-col sm:flex-row gap-2 sm:items-end'>
              <div className="flex flex-1 items-center justify-center">
                <button
                  onClick={() => {setSelection(0)}}
                  className={`w-1/2 border border-[#94a3b8] font-semibold p-2 rounded-l-lg text-base hover:bg-[#cbd5e1] text-[#1e293b] uppercase
                  transition duration-150 hover:bg-[#cbd5e1] hover:border border-inset border-[#346F3E]
                  ${
                    selection == 0 ? "bg-[#cbd5e1]" : "bg-white"
                  }`}
                >
                  Thresholds
                </button>
                <button
                  onClick={() => {setSelection(1)}}
                  className={`w-1/2 border border-[#94a3b8] font-semibold p-2 rounded-r-lg text-base hover:bg-[#cbd5e1] text-[#1e293b] uppercase
                  transition duration-150 hover:bg-[#cbd5e1] hover:border border-inset border-[#346F3E]
                  ${
                    selection == 1 ? "bg-[#cbd5e1]" : "bg-white"
                  }`}
                >
                  Units
                </button>
              </div>
              <div className="flex items-end sm:w-1/3 gap-2">
                <DropDownWithLabel
                  optionsList={profileList}
                  label="Profile"
                  tooltip="Save a profile and select it asper your needs"
                  val={currProfile}
                  onChange={(val) => {updateProfile(val)}}
                  edit={editProfileDopDown}
                  />
                <div className='flex border rounded-lg w-full'>
                  <button
                    onClick={() => setModalState(true)}
                    className="w-1/2 font-semibold py-2 rounded-l-lg border border-inset border-[#94a3b8] text-sm hover:bg-[#cbd5e1] text-[#1e293b] uppercase
                    hover:bg-[#DFFCE6] hover:border bg-white">
                        Add Profile
                  </button>
                  <button
                    onClick={() => removeProfile()}
                    className="w-1/2 font-semibold py-2 rounded-r-lg border border-inset border-[#94a3b8] text-sm hover:bg-[#cbd5e1] text-[#1e293b] uppercase 
                    hover:bg-[#FFE5E5] hover:border bg-white"
                    disabled={removeProfileLoad}>
                    {removeProfileLoad ? <Spinner/> : <span>Delete Profile</span>}
                  </button>
                </div>
              </div>
            </div>
        
            <div className="flex items-center justify-center">
              <img src={messagealert} className="w-10 h-10" />
              <p className="text-sm font-semibold text-gray-600 antialiased uppercase text-[#64748B]">
                Custom thresholds will affect MissionCast and Sensor based Alerting.
              </p>
            </div>
            {
              selection == 0 ?
              <ThresholdForm
                userThresholds={userThresholds}
                drafts={drafts}
                onParamChange={updateParamValue}
                onSave={handleThresholdSave}
                loadState={saveProfileLoad}
                updateState={updatedThresholdsState}
              />
              :
              <UnitsForm
                userThresholds={userThresholds}
                drafts={drafts}
                updateUnit={updateUnit}
                onSave={handleUnitSave}
                loadState={saveUnitsLoad}
                updateState={updatedUnitsState}
              />
            }
          </>
        }
        
      </div>
      {
        modalOpen &&
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className="absolute inset-0"
            onClick={() => {setModalState(false)}}
            aria-hidden="true"
          />
          <div className="flex flex-col gap-2 justify-center relative bg-[#F8F8F8] dark:bg-gray-800 rounded-lg h-auto shadow-lg w-1/4 p-6 z-10 md:h-min">
            <div className="flex items-center justify-between">
              <p className="text-sm md:text-base text-[#1e293b] uppercase font-semibold w-1/2">
                Add new Profile
              </p>
              <button
                className="flex text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-200"
                onClick={() => {setModalState(false)}}
              >
                {' '}
                &#x2715;{' '}
              </button>
            </div>
            <hr />
            <div className='flex gap-2 rounded-lg border border-[#94a3b8] p-2 items-center'>
                <div className="w-full">
                  <Input
                    placeHolder={'Profile Label'}
                    type={'text'}
                    val={profileFormInput}
                    onChange={(v) => {setProfileFormInput(v)}}
                    label={''}
                    tooltip={''}
                    edit={true}
                  />
                </div>
                <button className={clsx(
                    "items-center justify-center p-2 h-full w-1/4 uppercase text-base font-semibold rounded border border-[#346F3E] transition duration-150",
                    profileFormInput !== "" && "bg-[#DFFCE6] text-green-900 hover:bg-green-400",
                    profileFormInput === "" && "bg-gray-200 text-gray-500 cursor-not-allowed"
                  )}
                  onClick={handleNewProfile}
                  disabled={profileFormInput === ""}
                > 
                  {formLoad ? <Spinner className='h-full'/> : <span>Save</span>}
                </button>              
            </div>
          </div>
        </div>
      }
    </div>
  )
}
